import { Stack } from '@vtex/admin-ui'
import { useWatch } from '@vtex/admin-ui-form'
import { useDrawerSubForm } from 'hooks'
import { useMemo } from 'react'
import { useIntl } from 'react-intl'
import { SubscriptionType, type Subscription } from 'typings'
import {
  CancelButton,
  DismissButton,
  Drawer,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerSubtitle,
  DrawerTitle,
  SaveButton,
  type UseDrawerStateReturn,
} from '../../../../Drawer'
import { messages } from '../messages'
import { CycleFilter } from './CycleFilter'
import { Filters } from './Filters'
import { FrequencyFilter } from './FrequencyFilter'
import { TypeInput } from './TypeInput'
import { validationSuite } from './validation'

interface Props {
  state: UseDrawerStateReturn
  initialConfig: Subscription | undefined
  onSave: (config: Subscription | undefined) => void
}

const EMPTY_CONFIG: Subscription = Object.freeze({
  type: SubscriptionType.ALL,
  filter: {
    frequency: undefined,
    cycle: undefined,
  },
})

export function SubscriptionEditor(props: Props) {
  const { state, initialConfig, onSave } = props
  const { hide } = state

  const intl = useIntl()
  const { $t } = intl

  const config = useMemo(
    () => initialConfig ?? { ...EMPTY_CONFIG },
    [initialConfig]
  )

  const { drawerForm } = useDrawerSubForm<Subscription>({
    config,
    isOpen: state.open,
    validationFn: validationSuite(intl),
  })

  const type: SubscriptionType | undefined = useWatch({
    name: 'type',
    form: drawerForm,
  })

  const handleSave = async () => {
    const isValid = await drawerForm.trigger()
    if (!isValid) return

    onSave(drawerForm.getValues())
    hide()
  }

  const isCreating = initialConfig === undefined

  return (
    <Drawer state={state}>
      <DrawerHeader>
        <DrawerTitle>{$t(messages.title)}</DrawerTitle>
        <DrawerSubtitle>{$t(messages.editor.subtitle)}</DrawerSubtitle>
        <DismissButton onHide={state.hide} />
      </DrawerHeader>
      <DrawerContent>
        <Stack space="$space-4" fluid>
          <TypeInput name="type" state={drawerForm} />
          <Filters>
            <FrequencyFilter name="filter.frequency" form={drawerForm} />
            <CycleFilter
              name="filter.cycle"
              form={drawerForm}
              disabled={type === SubscriptionType.FIRST}
            />
          </Filters>
        </Stack>
      </DrawerContent>
      <DrawerFooter>
        <CancelButton onClick={hide} />
        <SaveButton isCreating={isCreating} onClick={handleSave} />
      </DrawerFooter>
    </Drawer>
  )
}
